/**
 * Display formatting.
 *
 * Money always carries its currency code (§9): currency is per-organization,
 * per-catalogue and per-line, so a bare number is genuinely ambiguous — 1,000
 * AED and 1,000 KWD differ by roughly 12×.
 */

// Gulf currencies with three decimal places.
const THREE_DECIMAL = new Set(['KWD', 'BHD', 'OMR', 'JOD', 'TND']);
// Currencies conventionally written without decimals.
const ZERO_DECIMAL = new Set(['IQD', 'JPY', 'KRW', 'VND']);

export function decimalsFor(currency) {
  if (THREE_DECIMAL.has(currency)) return 3;
  if (ZERO_DECIMAL.has(currency)) return 0;
  return 2;
}

/** `AED 1,250.00`. Pass `omitCode` for a column that already has a header. */
export function money(amount, currency, { omitCode = false, compact = false } = {}) {
  if (amount === null || amount === undefined || Number.isNaN(Number(amount))) return '—';

  const decimals = decimalsFor(currency);
  const value = Number(amount);

  // Compact only above 10k, and never below — "1.2k" where "1,240" fits is
  // a loss of precision for no gain in space.
  const formatted = compact && Math.abs(value) >= 10000
    ? new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(value)
    : new Intl.NumberFormat(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);

  if (omitCode || !currency) return formatted;
  return `${currency} ${formatted}`;
}

/** Thousands-separated integer. */
export function num(value, { compact = false } = {}) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  const n = Number(value);
  if (compact && Math.abs(n) >= 10000) {
    return new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(n);
  }
  return new Intl.NumberFormat().format(n);
}

export function percent(value, { decimals = 1 } = {}) {
  if (value === null || value === undefined) return '—';
  return `${Number(value).toFixed(decimals).replace(/\.0$/, '')}%`;
}

const DATE_OPTS = { day: '2-digit', month: 'short', year: 'numeric' };

export function date(value) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, DATE_OPTS);
}

export function dateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

/** `3h ago`, `12d ago`. Coarse on purpose — precision lives in the tooltip. */
export function relative(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';

  const minutes = Math.round((Date.now() - d.getTime()) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h ago`;

  const days = Math.round(hours / 24);
  if (days < 60) return `${days}d ago`;
  if (days < 730) return `${Math.round(days / 30)}mo ago`;
  return `${Math.round(days / 365)}y ago`;
}

/** `104.8h` → `4d 8h`. Turnaround numbers get large fast. */
export function duration(hours) {
  if (hours === null || hours === undefined) return '—';
  const h = Number(hours);
  if (h < 1) return `${Math.round(h * 60)}m`;
  if (h < 48) return `${Math.round(h * 10) / 10}h`;
  const days = Math.floor(h / 24);
  const rem = Math.round(h % 24);
  return rem ? `${days}d ${rem}h` : `${days}d`;
}

/** `in_progress` → `In progress`. */
export function humanize(value) {
  if (!value) return '—';
  const s = String(value).replace(/[_-]+/g, ' ').trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function initials(nameOrEmail) {
  const s = String(nameOrEmail || '?').trim();
  return s
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join('') || '?';
}

/** Deterministic accent per string — used to tint tenant avatars consistently. */
const ACCENTS = ['blue', 'teal', 'orange', 'green', 'red'];
export function accentFor(seed) {
  const s = String(seed || '');
  let hash = 0;
  for (let i = 0; i < s.length; i += 1) hash = (hash * 31 + s.charCodeAt(i)) >>> 0;
  return ACCENTS[hash % ACCENTS.length];
}

export function truncate(value, length = 28) {
  const s = String(value ?? '');
  return s.length > length ? `${s.slice(0, length - 1)}…` : s;
}
